import React from 'react';
import { DollarSign, Users, Code, Zap, Shield, TrendingUp, Smartphone, Globe, Database, ArrowRight, Award, Clock, Star } from 'lucide-react';

const WhyChoosePage: React.FC = () => {
  const mainReasons = [
    {
      icon: DollarSign,
      title: '$50K+ Development Investment',
      subtitle: 'Get years of development for a fraction of the cost',
      description: 'This platform represents over $50K in development investment. You\'re getting a battle-tested, enterprise-grade system that would cost tens of thousands to build from scratch.',
      benefits: [
        'Save 2-3 years of development time',
        'Avoid the risk of failed development projects',
        'Get proven features that work',
        'No need to hire expensive development teams'
      ]
    },
    {
      icon: Users,
      title: 'Proven Platform',
      subtitle: 'Battle-tested in real business environments',
      description: 'This isn\'t an untested product. Our platform is currently being used by businesses across multiple industries, processing thousands of transactions daily.',
      benefits: [
        'Battle-tested in real business environments',
        'Continuously improved based on user feedback',
        'Proven scalability and reliability',
        'Success stories across multiple industries'
      ]
    },
    {
      icon: Code,
      title: 'Complete Ownership',
      subtitle: 'Full source code and white-label rights',
      description: 'Unlike SaaS subscriptions, you own the platform completely. No ongoing fees, no restrictions, no vendor lock-in. You have full control over your business platform.',
      benefits: [
        'No monthly fees or ongoing costs',
        'Complete customization freedom',
        'Build unlimited custom solutions for clients',
        'No vendor dependency'
      ]
    }
  ];

  const businessModelFlexibility = [
    {
      model: 'SaaS Subscription',
      description: 'Customize and offer monthly/yearly subscriptions under your brand',
      potential: '$50-500/month per client'
    },
    {
      model: 'One-Time License',
      description: 'Rebrand and provide one-time solutions to businesses',
      potential: '$5,000-50,000 per license'
    },
    {
      model: 'Custom Enterprise',
      description: 'Transform into custom enterprise solutions for large organizations',
      potential: '$25,000-250,000 per project'
    },
    {
      model: 'Niche-Specific Platform',
      description: 'Fully customize for specific industries and launch as your own branded SaaS',
      potential: 'Unlimited scalability'
    }
  ];

  const technicalExcellence = [
    {
      category: 'Architecture',
      features: [
        'Cloud-ready deployment',
        'Microservices architecture',
        'Horizontal scaling capability',
        'Load balancer ready'
      ]
    },
    {
      category: 'Performance',
      features: [
        'Optimized database queries',
        'Caching systems included',
        'CDN integration ready',
        'Mobile-first responsive design'
      ]
    },
    {
      category: 'Security',
      features: [
        'Bank-level encryption',
        'GDPR compliance built-in',
        'Regular security updates',
        'Audit trail functionality'
      ]
    },
    {
      category: 'Integrations',
      features: [
        'Payment gateway APIs',
        'Calendar system integrations'
      ]
    }
  ];

  const competitorComparison = [
    {
      feature: 'Upfront Cost',
      us: '$799-1,499 one-time',
      competitors: '$50,000-500,000+',
      advantage: 'Save 95%+ on initial investment'
    },
    {
      feature: 'Monthly Fees',
      us: '$0 forever',
      competitors: '$500-5,000/month',
      advantage: 'No ongoing costs ever'
    },
    {
      feature: 'Customization',
      us: 'Unlimited customization',
      competitors: 'Limited or expensive',
      advantage: 'Complete control over features'
    },
    {
      feature: 'Client Limits',
      us: 'Unlimited clients',
      competitors: 'Per-user pricing',
      advantage: 'Scale without limits'
    },
    {
      feature: 'Revenue Model',
      us: 'Keep 100% of revenue',
      competitors: 'Revenue sharing',
      advantage: 'All profits are yours'
    }
  ];


  return (
    <div className="min-h-screen bg-gradient-innovation text-white relative overflow-hidden">
      {/* Global Animated Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-transparent"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '4s'}}></div>
      <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '6s'}}></div>
      
      {/* Hero Section */}
      <section className="py-20 relative z-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-cyan-300 bg-clip-text text-transparent">
            Why Smart Entrepreneurs<br />Choose Our Platform
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto mb-8 leading-relaxed">
            Skip years of development and <span className="text-cyan-300 font-semibold">millions in costs</span>. Get a proven, 
            enterprise-grade platform that's already generating <span className="text-yellow-300 font-semibold">$40K+ monthly for our partners</span>. 
            This is the smart way to own your business technology.
          </p>
          <div className="mb-8 flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-400/20 backdrop-blur-xl border border-green-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-green-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <Award className="h-5 w-5 text-green-400" />
                Proven Success Formula
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-xl border border-blue-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <Clock className="h-5 w-5 text-blue-400" />
                Years of Dev Time Saved
              </div>
            </div>
            <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-xl border border-yellow-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-yellow-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                <Star className="h-5 w-5 text-yellow-400" />
                Enterprise-Grade Quality
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Value Propositions */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {mainReasons.map((reason, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className="bg-primary-100 p-4 rounded-xl mr-4">
                      <reason.icon className="h-10 w-10 text-primary-600" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">{reason.title}</h2>
                      <p className="text-xl text-primary-600 font-medium">{reason.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-lg text-slate-200 mb-6">{reason.description}</p>
                  <ul className="space-y-3">
                    {reason.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                        <span className="text-slate-200">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl shadow-xl p-8 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">
                      {index === 0 ? '$50K+' : index === 1 ? '500+' : '100%'}
                    </div>
                    <div className="text-slate-300">
                      {index === 0 ? 'Development Value' : index === 1 ? 'Active Users' : 'Ownership'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Model Flexibility */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Unlimited Business Model Flexibility
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Choose how you want to monetize your platform - or use multiple models simultaneously
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {businessModelFlexibility.map((model, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-xl font-bold text-white mb-2">{model.model}</h3>
                <p className="text-slate-300 mb-4">{model.description}</p>
                <div className="bg-primary-100 p-3 rounded-lg">
                  <span className="text-primary-800 font-semibold">Revenue Potential: </span>
                  <span className="text-primary-900 font-bold">{model.potential}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-sm border border-green-300/30 rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 font-bold">LIVE SUCCESS STORY</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  ClinicIT - $1,299 Investment Success
                </h3>
                <p className="text-slate-300">Medical practice platform serving real clinics</p>
              </div>
              <a 
                href="https://clinicit.live" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
              >
                Open Full Site
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="bg-slate-800 rounded px-3 py-1 text-slate-300 text-sm font-mono">
                  https://clinicit.live
                </div>
              </div>
              
              <div className="relative">
                <iframe
                  src="https://clinicit.live"
                  className="w-full h-[500px] rounded-lg border border-slate-600/50"
                  title="ClinicIT Success Story Demo"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-slate-900/10 pointer-events-none rounded-lg"></div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-cyan-400 font-bold text-lg">$1,299 Investment</div>
                <div className="text-slate-300 text-sm">Complete Platform Ownership</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-cyan-400 font-bold text-lg">Medical Niche</div>
                <div className="text-slate-300 text-sm">HIPAA-Compliant Solution</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-cyan-400 font-bold text-lg">Live Production</div>
                <div className="text-slate-300 text-sm">Serving Real Clinics</div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-400/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Award className="h-6 w-6 text-cyan-400" />
                <h4 className="text-lg font-bold text-white">Proof of Platform Value</h4>
              </div>
              <p className="text-slate-300 text-sm">
                This medical practice management system demonstrates the real-world value of our platform. 
                One $1,299 investment created a professional, industry-specific solution now serving actual medical practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Excellence */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Built for Scale & Performance
            </h2>
            <p className="text-xl text-slate-300">
              Enterprise-grade technology stack designed to handle growth from day one
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technicalExcellence.map((category, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-white mb-4">{category.category}</h3>
                <ul className="space-y-2">
                  {category.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-slate-200">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 shadow-sm">
              <Database className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Scalable to 10,000+ Users</h3>
              <p className="text-slate-300 text-sm">Architecture designed to handle massive growth</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 shadow-sm">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Bank-Level Security</h3>
              <p className="text-slate-300 text-sm">Enterprise security standards built-in</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 shadow-sm">
              <Smartphone className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Mobile-First Design</h3>
              <p className="text-slate-300 text-sm">Perfect experience on all devices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Competitor Comparison */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How We Compare to Alternatives
            </h2>
            <p className="text-xl text-slate-300">
              See why our white-label solution is the smartest choice
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center font-semibold">Our Solution</th>
                    <th className="px-6 py-4 text-center font-semibold">Custom Development</th>
                    <th className="px-6 py-4 text-center font-semibold">Our Advantage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {competitorComparison.map((item, index) => (
                    <tr key={index} className="hover:bg-white/5 backdrop-blur-sm border border-white/10">
                      <td className="px-6 py-4 font-medium text-white">{item.feature}</td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">{item.us}</td>
                      <td className="px-6 py-4 text-center text-red-600">{item.competitors}</td>
                      <td className="px-6 py-4 text-center text-white font-medium">{item.advantage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>


      {/* ROI Calculator */}
      <section className="py-20 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Calculate Your ROI
            </h2>
            <p className="text-xl text-slate-300">
              See how quickly you can recover your investment
            </p>
          </div>

          <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl p-8">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-slate-900 mb-2">$1,299</div>
                <div className="text-slate-700">One-time investment</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-700 mb-2">$200</div>
                <div className="text-slate-700">Average monthly per client</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-700 mb-2">8 Clients</div>
                <div className="text-slate-700">To break even in 1 month</div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <p className="text-lg text-slate-700 mb-4">
                With just 10 clients at $200/month, you'll generate $24,000 in annual recurring revenue.
                That's a <span className="font-bold text-green-700">1,500%+ ROI</span> in the first year alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 relative z-10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Ready to Own Your Platform?
          </h2>
          <p className="text-xl mb-8 text-slate-300">
            Join smart entrepreneurs who chose ownership over subscriptions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/pricing"
              className="bg-yellow-400 text-primary-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-300 transition-colors duration-200"
            >
              Get Started Today
            </a>
            <a
              href="/demo"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-white/5 backdrop-blur-sm border border-white/10 hover:text-primary-600 transition-colors duration-200"
            >
              See Demo First
            </a>
          </div>
          <p className="text-sm text-blue-200 mt-4">
            Instant access • Complete ownership • No questions asked
          </p>
        </div>
      </section>
    </div>
  );
};

export default WhyChoosePage;

